import React from "react";
import { Message } from "../../../types/chat";
import MarkdownRenderer from "../../ui/MarkdownRenderer";

interface MessageBubbleProps {
  message: Message;
  /** 스트리밍 중인 메시지인지 여부 (AI 메시지에만 해당) */
  isStreaming?: boolean;
}

/**
 * 첫 토큰 수신 전 로딩 인디케이터
 * TI-01: "AI가 답변 중입니다..." 텍스트 표시
 * TI-02: 스피너 애니메이션으로 로딩 상태 표시
 * TI-03: 첫 토큰 수신 시 자동 제거 (isWaitingForFirstToken 조건으로 처리)
 */
const TypingIndicator: React.FC = () => (
  <div className="flex items-center space-x-2 py-0.5">
    {/* 스피너 애니메이션 */}
    <svg
      className="w-4 h-4 text-blue-500 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
    <span className="text-sm text-gray-500">AI가 답변 중입니다...</span>
  </div>
);

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isStreaming = false,
}) => {
  const isUser = message.role === "USER";
  const formattedTime = new Date(message.createdAt).toLocaleTimeString(
    "ko-KR",
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  );

  const isWaitingForFirstToken = isStreaming && !message.content;

  return (
    <div className={`flex mb-4 ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] ${isUser ? "order-2" : "order-1"}`}>
        {!isUser && (
          <div className="flex items-center mb-1">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mr-2">
              <span className="text-white text-xs font-bold">AI</span>
            </div>
            <span className="text-xs text-gray-500">AI홈닥터</span>
          </div>
        )}
        <div
          className={`rounded-2xl px-4 py-3 shadow-sm ${
            isUser
              ? "bg-blue-500 text-white rounded-br-md"
              : "bg-gray-100 text-gray-800 rounded-bl-md"
          }`}
        >
          {isWaitingForFirstToken ? (
            <TypingIndicator />
          ) : isUser ? (
            <p className="text-sm leading-relaxed whitespace-pre-wrap">
              {message.content}
            </p>
          ) : (
            <div className={isStreaming && message.content ? "streaming-cursor" : ""}>
              <MarkdownRenderer content={message.content} />
            </div>
          )}
        </div>
        {!isStreaming && (
          <div
            className={`mt-1 text-xs text-gray-400 ${
              isUser ? "text-right" : "text-left"
            }`}
          >
            {formattedTime}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
